import React, { useState, useEffect } from "react";
import DataFetchHookOI from "../../helper/DataFetchHookOI";
import PermissionsRequired from "../../helper/PermissionsRequired";
import { Table, PageHeader, Button, Card, Form, Select, Row, Col, Input, Modal, Descriptions, Space, Divider } from "antd";
import { SearchOutlined, FileSearchOutlined } from "@ant-design/icons";
import moment from "moment";
import AssociationsChecker from "./../../helper/AssociationsChecker";
import CustomPaginationV2 from "../../helper/Table/CustomPaginationV2";
import CustomDateRangeV3 from "../../helper/Table/CustomDateRangeV3";
import SortOrder from "../../helper/Table/SortOrder";
import ExportModalV3 from "../../helper/ExportModalV3";
import { API_BASE_URL } from "../../config/config";
import Cookies from "js-cookie";
import createExportDateHandler from "../../helper/createExportDateHandler";

const { Option } = Select;
const DATA_URL = "/api/v1/txshield/notifications";
const DATA_URL_EXPORT = API_BASE_URL + "/v3/txfind/notificationreports/";
const MAX_DATERANGE = 12;
const START_DATE = moment().subtract(MAX_DATERANGE, "months");
const END_DATE = moment();

const Notifications = (props) => {
    const defaultSortInfo = {
        columnKey: 'caseId',
        order: 'descend'
    };

    // Initial request data with numeric page and size values
    const initialRequest = {
        dateFrom: START_DATE.format("YYYY-MM-DD 00:00:00"),
        dateTo: END_DATE.format("YYYY-MM-DD 23:59:59"),
    };

    const [{ data, totalResult, isLoading, requestData }, , setRequestData] = DataFetchHookOI(
        DATA_URL, 
        "POST", 
        null, 
        initialRequest
    );
    
    const [form] = Form.useForm();
    const [sortedInfo, setSortedInfo] = useState(defaultSortInfo);
    const [dates, setDates] = useState([START_DATE, END_DATE]);
    const [currentPage, setCurrentPage] = useState(1);
    const [exportRequestData, setExportRequestData] = useState({});
    const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const { t } = props;
    
    // Update the initFormData to use oiAssociation instead of an empty array
    const initFormData = {
        keyword: null,
        field: 'caseId',
        dateRange: [START_DATE, END_DATE],
    };

    // Ensure pagination state is synchronized with requestData
    useEffect(() => {
        if (requestData.page && parseInt(requestData.page) !== parseInt(currentPage)) {
            setCurrentPage(parseInt(requestData.page));
        }
    }, [requestData.page, currentPage]);

    useEffect(() => {
        formValInit();
        
        // Initialize with date range
        const initialStartDate = START_DATE.format("YYYY-MM-DD 00:00:00");
        const initialEndDate = END_DATE.format("YYYY-MM-DD 23:59:59");
        
        // Send initial search with dates to ensure data loads
        setRequestData({
            ...initialRequest,
            dateFrom: initialStartDate,
            dateTo: initialEndDate
        });
        
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setExportRequestData({ ...requestData, token: Cookies.get("txfind_token")});
    }, [requestData]);

    const handleChange = (pagination, filters, sorter) => {
        setSortedInfo(sorter);
    };

    const formValInit = () => {
        form.setFieldsValue(initFormData);
    };

    const clearAll = () => {
        const resetData = { ...initFormData };
        
        setCurrentPage(1);
        
        // When clearing, set dateRange to the initial date range
        const initialStartDate = START_DATE.format("YYYY-MM-DD 00:00:00");
        const initialEndDate = END_DATE.format("YYYY-MM-DD 23:59:59");
        
        setRequestData({ 
            ...requestData, 
            ...resetData, 
            dateFrom: initialStartDate, 
            dateTo: initialEndDate, 
            page: 1,
            size: parseInt(requestData.size) || 20,
            order: 'DESC'
        });
        
        form.setFieldsValue({
            ...resetData,
            dateRange: [START_DATE, END_DATE]
        });
    };

    const onFinish = () => {
        // Get form values
        const currentValues = form.getFieldsValue();
        
        // Create variables for date range filtering
        let dateFrom = null;
        let dateTo = null;
        
        // Handle date range properly
        if (currentValues.dateRange && Array.isArray(currentValues.dateRange) && currentValues.dateRange.length === 2) {
            if (currentValues.dateRange[0]) {
                // Format as YYYY-MM-DD 00:00:00 for start date
                dateFrom = moment(currentValues.dateRange[0]).format("YYYY-MM-DD 00:00:00");
            }
            
            if (currentValues.dateRange[1]) {
                // Format as YYYY-MM-DD 23:59:59 for end date
                dateTo = moment(currentValues.dateRange[1]).format("YYYY-MM-DD 23:59:59");
            }
        }
        
        // Build search request with explicit date parameters
        const searchData = {
            page: 1,
            size: parseInt(requestData.size) || 20,
            order: sortedInfo.order === 'ascend' ? 'ASC' : 'DESC',
            keyword: currentValues.keyword,
            field: currentValues.field,
            dateFrom: dateFrom,
            dateTo: dateTo
        };
                
        // Update state and trigger search
        setCurrentPage(1);
        setRequestData(searchData);
    };

    const showDetailModal = (record) => {
        console.log(record);
        setSelectedNotification(record);
        setIsDetailModalVisible(true);
    };

    const handleDetailModalCancel = () => {
        setIsDetailModalVisible(false);
        setSelectedNotification(null);
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return "";
        return moment(dateValue).format("YYYY-MM-DD HH:mm:ss");
    };

    const formatAmount = (amountObj) => {
        if (!amountObj) return "";
        return `${amountObj.amount || 0} ${amountObj.currency || ''}`;
    };

    const NotificationDetailModal = () => {
        if (!selectedNotification) return null;

        return (
            <Modal
                title={`Notification Details - ${selectedNotification.caseId}`}
                visible={isDetailModalVisible}
                onCancel={handleDetailModalCancel}
                width={800}
                footer={[
                    <Button key="close" onClick={handleDetailModalCancel}>
                        Close
                    </Button>
                ]}
            >
                <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <Descriptions title="Case Information" bordered column={2} size="small">
                        <Descriptions.Item label="Case ID">{selectedNotification.caseId || ""}</Descriptions.Item>
                        <Descriptions.Item label="Case Type">{selectedNotification.caseType || ""}</Descriptions.Item>
                        <Descriptions.Item label="Case Status">{selectedNotification.caseStatus || ""}</Descriptions.Item>
                        <Descriptions.Item label="Case Event">{selectedNotification.caseEvent || ""}</Descriptions.Item>
                        <Descriptions.Item label="Case Date">{formatDate(selectedNotification.caseDate)}</Descriptions.Item>
                        <Descriptions.Item label="Source">{selectedNotification.source || ""}</Descriptions.Item>
                        <Descriptions.Item label="Reason Code">{selectedNotification.reasonCode || ""}</Descriptions.Item>
                        <Descriptions.Item label="Linked Insight ID">{selectedNotification.linkedInsightId || ""}</Descriptions.Item>
                        <Descriptions.Item label="Received On">{formatDate(selectedNotification.receivedOn)}</Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Descriptions title="Entity Information" bordered column={2} size="small">
                        <Descriptions.Item label="Partner ID">
                            {selectedNotification.verifiEntityInfo?.partnerId || ""}
                        </Descriptions.Item>
                        <Descriptions.Item label="Client ID">
                            {selectedNotification.verifiEntityInfo?.clientId || ""}
                        </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Descriptions title="Card Information" bordered column={2} size="small">
                        <Descriptions.Item label="Card BIN">{selectedNotification.cardBin || ""}</Descriptions.Item>
                        <Descriptions.Item label="Card Last 4">{selectedNotification.cardLast4 || ""}</Descriptions.Item>
                        <Descriptions.Item label="Payment Type">{selectedNotification.paymentType || ""}</Descriptions.Item>
                        <Descriptions.Item label="ARN">{selectedNotification.arn || ""}</Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Descriptions title="Transaction Information" bordered column={2} size="small">
                        <Descriptions.Item label="Transaction ID">{selectedNotification.transactionId || ""}</Descriptions.Item>
                        <Descriptions.Item label="Transaction Type">{selectedNotification.transactionType || ""}</Descriptions.Item>
                        <Descriptions.Item label="Transaction Date">{formatDate(selectedNotification.transactionDate)}</Descriptions.Item>
                        <Descriptions.Item label="Auth Code">{selectedNotification.authCode || ""}</Descriptions.Item>
                        <Descriptions.Item label="Transaction Amount">
                            {formatAmount(selectedNotification.transactionAmount)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Destination Amount">
                            {formatAmount(selectedNotification.destinationAmount)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Purchase Identifier">{selectedNotification.purchaseIdentifier || ""}</Descriptions.Item>
                        <Descriptions.Item label="MCC">{selectedNotification.mcc || ""}</Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Descriptions title="Merchant Information" bordered column={2} size="small">
                        <Descriptions.Item label="Terminal ID">{selectedNotification.terminalId || ""}</Descriptions.Item>
                        <Descriptions.Item label="Acquirer BIN">{selectedNotification.acquirerBin || ""}</Descriptions.Item>
                        <Descriptions.Item label="Card Acceptor ID">{selectedNotification.cardAcceptorId || ""}</Descriptions.Item>
                        <Descriptions.Item label="Payment Descriptor">{selectedNotification.paymentDescriptor || ""}</Descriptions.Item>
                        <Descriptions.Item label="Payment Descriptor Contact">
                            {selectedNotification.paymentDescriptorContact || ""}
                        </Descriptions.Item>
                        <Descriptions.Item label="MCSN">{selectedNotification.mcsn || ""}</Descriptions.Item>
                        <Descriptions.Item label="POS Entry Mode Code">{selectedNotification.posEntryModeCode || ""}</Descriptions.Item>
                        <Descriptions.Item label="ECI">{selectedNotification.eci || ""}</Descriptions.Item>
                    </Descriptions>
                </div>
            </Modal>
        );
    };

    const columns = [
        {
            dataIndex: "caseId",
            key: "caseId",
            title: t("tables.fields.caseId"),
            sorter: {
                compare: (a, b) => String(a.caseId).localeCompare(String(b.caseId)),
            },
            sortOrder: SortOrder(sortedInfo, "caseId"),
        },
        {
            dataIndex: "caseStatus",
            key: "caseStatus",
            title: t("tables.fields.caseStatus"),
            sorter: {
                compare: (a, b) => String(a.caseStatus).localeCompare(String(b.caseStatus)),
            },
            sortOrder: SortOrder(sortedInfo, "caseStatus"),
        },
        {
            dataIndex: "caseType",
            key: "caseType",
            title: t("tables.fields.caseType"),
            sorter: {
                compare: (a, b) => String(a.caseType).localeCompare(String(b.caseType)),
            },
            sortOrder: SortOrder(sortedInfo, "caseType"),
        },
        {
            dataIndex: "receivedOn",
            key: "receivedOn",
            title: t("tables.fields.receivedOn"),
            sorter: {
                compare: (a, b) => String(a.receivedOn).localeCompare(String(b.receivedOn)),
            },
            sortOrder: SortOrder(sortedInfo, "receivedOn"),
            render: (receivedOn) => moment(receivedOn).format("YYYY-MM-DD HH:mm:ss"),
        },
        {
            dataIndex: "action",
            key: "action",
            title: t("tables.fields.action") || "Action",
            align: "center",
            width: 120,
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type="link"
                        icon={<FileSearchOutlined style={{ color: "rgba(0, 0, 0, 0.85)", fontSize: "18px" }} />}
                        onClick={() => showDetailModal(record)}
                        title="View Details"
                    />
                </Space>
            ),
        },
    ];

    /**
     * callback to reflect the changes on dates to the passing request data
     * 
     * @param array values 
     */
    const handleDateChange = createExportDateHandler(requestData);

    const AdvancedSearchForm = () => (
        <div className="search-wrap">
            <Card title={t("tables.actions.search")} size="small">
                <Form form={form} size="large" name="advanced_search" className="ant-advanced-search-form" onFinish={onFinish}>
                    <Row gutter={[16, 0]}>
                        <Col className="gutter-row" sm={{ span: 24 }} md={{ span: 12 }}>
                            <Form.Item name="dateRange">
                                <CustomDateRangeV3 t={t} state={{ dates, setDates }} unit="months" limit={MAX_DATERANGE} onChange={handleDateChange} />
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 5 }}>
                            <Form.Item name="field">
                                <Select 
                                    showSearch
                                    style={{ width: "100%" }} 
                                    placeholder={t("tables.fields.field")}>
                                    <Option value="caseId">{t("tables.fields.caseId")}</Option>
                                    <Option value="caseStatus">{t("tables.fields.caseStatus")}</Option>
                                    <Option value="caseType">{t("tables.fields.caseType")}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 7 }}>
                            <Form.Item name="keyword">
                                <Input placeholder={t("tables.fields.keyword")} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                        <Col
                            className="gutter-row"
                            style={{ marginBottom: "8px" }}
                            xs={{ span: 24 }}
                            sm={{ span: 12 }}
                            md={{ span: 6 }}
                            lg={{ span: 6 }}
                            xl={{ span: 3 }}
                        >
                            <Form.Item>
                                <Button type="secondary" onClick={() => clearAll()} block>
                                    {t("tables.actions.clear_all")}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 4 }}>
                            <Form.Item>
                                <Button type="primary" htmlType="submit" block>
                                    <SearchOutlined /> {t("tables.actions.search")}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );

    // Export configuration
    const exportHeadersOptional = [];
    const exportHeaders = [
        { label: "Case ID", key: "caseId" },
        { label: "Case Status", key: "caseStatus" },
        { label: "Case Date", key: "caseDate" },
        { label: "Case Type", key: "caseType" },
    ];

    const getHeaderTable = () => (
        <div className="custom-table--header d-flex justify-content-between align-items-center">
            <h3 className="table-title">{t("contents.oi.notifications")}</h3>
            <ExportModalV3
                t={t}
                keyname="export_all"
                title={t("tables.actions.export")}
                data={exportRequestData}
                data_url={DATA_URL_EXPORT}
                headers={exportHeaders}
                fieldHeadersOptional={exportHeadersOptional}
                filename={`notifications_${moment().format("YYMMDD_HHmmss_SSSS")}.csv`}
                style={{ marginLeft: 8 }}
            />
        </div>
    );

    // Use the proper numeric values for pagination
    const paginationConfig = CustomPaginationV2({
        totalResult: parseInt(totalResult) || 0,
        currentPage: parseInt(currentPage) || 1,
        requestData: {
            ...requestData,
            page: parseInt(requestData.page) || 1,
            size: parseInt(requestData.size) || 20
        },
        setRequestData,
        setCurrentPage
    });

    return PermissionsRequired("SYSADMIN") || AssociationsChecker("OI") ? (
        <div>
            <PageHeader title={t("contents.oi.notifications")}></PageHeader>
            {AdvancedSearchForm()}
            <NotificationDetailModal />
            <Table
                loading={isLoading}
                title={() => getHeaderTable()}
                rowKey="caseId"
                columns={columns}
                size="small"
                scroll={{ x: "auto" }}
                className="table-v3 custom-pagination"
                dataSource={data.datalist}
                onChange={handleChange}
                pagination={paginationConfig}
            />
        </div>
    ) : null;
};

export default Notifications;